/**
 * The one IndexedDB handle.
 *
 * Routes and tracks live in the same database, and that means exactly one place
 * may declare its version and its upgrade path. Two modules opening `webnav`
 * with different versions is not a subtle bug — the second open blocks forever
 * behind the first connection, and the app hangs on whichever panel is opened
 * second, with nothing in the console.
 *
 * The upgrade creates only what is missing, so a browser holding v1 (routes
 * only, from before tracks existed) gains the track store and keeps its routes.
 * Recreating the routes store on upgrade would silently delete every saved
 * passage plan on the first launch after an update.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import type { Route } from '../core/route';
import type { Track } from '../core/track';

const DB_NAME = 'webnav';

/**
 * 1 — routes.
 * 2 — tracks added.
 */
const DB_VERSION = 2;

export const ROUTE_STORE = 'routes';
export const TRACK_STORE = 'tracks';

export interface WebNavDB extends DBSchema {
  routes: {
    key: string;
    value: Route;
    indexes: { 'by-updated': number };
  };
  tracks: {
    key: string;
    value: Track;
    indexes: { 'by-updated': number };
  };
}

let dbPromise: Promise<IDBPDatabase<WebNavDB>> | null = null;

export function db(): Promise<IDBPDatabase<WebNavDB>> {
  if (!dbPromise) {
    dbPromise = openDB<WebNavDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(ROUTE_STORE)) {
          database
            .createObjectStore(ROUTE_STORE, { keyPath: 'id' })
            .createIndex('by-updated', 'updatedAt');
        }
        if (!database.objectStoreNames.contains(TRACK_STORE)) {
          database
            .createObjectStore(TRACK_STORE, { keyPath: 'id' })
            .createIndex('by-updated', 'updatedAt');
        }
      },
    });
  }
  return dbPromise;
}

/** True when this browser can persist anything at all. */
export function isStorageAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}
